<template>
  <div class="order-list-component">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading"></i>
      <p>正在加载订单数据...</p>
    </div>

    <!-- 空状态 -->
    <div v-else-if="orders.length === 0" class="empty-container">
      <el-empty description="暂无订单数据">
        <el-button type="primary" @click="$emit('refresh')">重新加载</el-button>
      </el-empty>
    </div>

    <!-- 订单列表 -->
    <div v-else class="order-list">
      <div
        v-for="order in orders"
        :key="order.id || order.orderId"
        class="order-card"
      >
        <!-- 订单头部 -->
        <div class="order-header">
          <div class="order-header-left">
            <span class="order-number">{{ order.orderNo }}</span>
            <el-button
              type="text"
              size="mini"
              @click="copyText(order.orderNo)"
              class="copy-btn"
              title="复制"
            >
              <i class="el-icon-document-copy"></i>
            </el-button>
          </div>
          <span :class="['order-status', getOrderStatusClass(order.orderStatus)]">{{ order.orderStatusName }}</span>
        </div>

        <!-- 订单内容 -->
        <div class="order-content">
          <!-- 商品信息 -->
          <div v-if="order.goodsTitle || order.goods" class="product-info">
            <div class="product-image">
              <img
                :src="order.goodsMainImgThumbnail || order.goods?.mainImg || ''"
                :alt="order.goodsSubTitle || order.goods?.subTitle || ''"
                @error="handleImageError"
              />
            </div>
            <div class="product-details">
              <div class="product-name">{{ order.goodsTitle || order.goods?.title || '未命名商品' }}</div>
              <div v-if="order.goodsSubTitle || order.goods?.subTitle" class="product-subtitle">
                {{ order.goodsSubTitle || order.goods?.subTitle }}
              </div>
              <div class="product-sku">
                <span class="label">SKU：</span>
                <span class="value">{{ order.goodsSku }}</span>
                <el-button
                  type="text"
                  size="mini"
                  @click="copyText(order.goodsSku)"
                  class="copy-btn-inline"
                  title="复制"
                >
                  <i class="el-icon-document-copy"></i>
                </el-button>
              </div>
              <div class="product-sku">
                <span class="label">下单时间：</span>
                <span class="value">{{ order.createTime }}</span>
                <!-- <el-button
                  type="text"
                  size="mini"
                  @click="copyText(order.goodsSku)"
                  class="copy-btn-inline"
                >
                  复制
                </el-button> -->
              </div>
            </div>
          </div>
          <div v-else class="product-unavailable">商品已下架/其他</div>

          <!-- 订单详细信息 - Tab 样式 -->
          <div class="order-info">
            <div class="info-tabs">
              <div 
                class="tab-item" 
                :class="{ active: getActiveTab(order) === 'order' }"
                @click="toggleTab(order, 'order')"
              >
                订单信息
              </div>
              <div 
                class="tab-item" 
                :class="{ active: getActiveTab(order) === 'logistics' }"
                @click="handleLogisticsTabClick(order)"
              >
                物流信息
              </div>
            </div>
            
            <!-- 订单信息内容 -->
            <div class="tab-content" v-show="getActiveTab(order) === 'order'">
              <div class="info-row">
                <span class="label">收货信息：</span>
                <div>
                  <span class="value">
                    {{ order.idCardName }}, {{ order.phone }},
                    {{ order.province }}, {{ order.city }}, {{ order.town }},
                    {{ order.address }}
                  </span>
                  <el-button
                    type="text"
                    size="mini"
                    @click="toggleDetailInfo(order)"
                    class="copy-btn-inline"
                    :title="order.ditalStatus == 0 ? '查看详细信息' : '隐藏详细信息'"
                  >
                    <i :class="order.ditalStatus == 0 ? 'el-icon-search' : 'el-icon-close'"></i>
                  </el-button>
                  <el-button
                    v-if="order.ditalStatus == 1"
                    type="text"
                    size="mini"
                    @click="copyAddressInfo(order)"
                    class="copy-btn-inline"
                    title="复制"
                  >
                    <i class="el-icon-document-copy"></i>
                  </el-button>
                </div>
              </div>

              <div class="info-row" v-if="order.sysOrderRemark">
                <span class="label">订单备注：</span>
                <div>
                  <span class="value">{{ order.sysOrderRemark }}</span>
                  <el-button
                    type="text"
                    size="mini"
                    @click="copyText(order.sysOrderRemark)"
                    class="copy-btn-inline"
                    title="复制"
                  >
                    <i class="el-icon-document-copy"></i>
                  </el-button>
                </div>
              </div>

              <div class="info-row">
                <span class="label">下单方式：</span>
                <div>
                  <el-tag v-if="order.sourceType == '2'">代客下单</el-tag>
                  <el-tag v-else-if="order.sourceType == '1'" type="success">小程序下单</el-tag>
                  <el-tag v-else type="info">未知</el-tag>
                </div>
              </div>
            </div>

            <!-- 物流信息内容 -->
            <div class="tab-content" v-show="getActiveTab(order) === 'logistics'">
              <div class="info-row">
                <span class="label">生产号码：</span>
                <div>
                  <span class="value">
                    {{ order.logisticsInfo?.productionNumber || "暂无数据" }}
                  </span>
                  <el-button
                    v-if="order.logisticsInfo?.productionNumber"
                    type="text"
                    size="mini"
                    @click="copyText(order.logisticsInfo.productionNumber)"
                    class="copy-btn-inline"
                    title="复制"
                  >
                    <i class="el-icon-document-copy"></i>
                  </el-button>
                </div>
              </div>

              <div class="info-row">
                <span class="label">物流公司：</span>
                <div>
                  <span class="value">
                    {{ order.logisticsInfo?.logisticsCompany || "暂无数据" }}
                  </span>
                  <el-button
                    v-if="order.logisticsInfo?.logisticsCompany"
                    type="text"
                    size="mini"
                    @click="copyText(order.logisticsInfo.logisticsCompany)"
                    class="copy-btn-inline"
                    title="复制"
                  >
                    <i class="el-icon-document-copy"></i>
                  </el-button>
                </div>
              </div>

              <div class="info-row">
                <span class="label">物流单号：</span>
                <div>
                  <span class="value">
                    {{ order.logisticsInfo?.trackingNumber || "暂无数据" }}
                  </span>
                  <el-button
                    v-if="order.logisticsInfo?.trackingNumber"
                    type="text"
                    size="mini"
                    @click="copyText(order.logisticsInfo.trackingNumber)"
                    class="copy-btn-inline"
                    title="复制"
                  >
                    <i class="el-icon-document-copy"></i>
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div
      class="pagination-container"
      v-if="showPagination && orders.length > 0"
    >
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.pageNum"
        :page-sizes="[5, 10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="sizes, prev, pager, next"
        :total="pagination.total"
      />
    </div>
  </div>
</template>

<script>
import { getOrderStatusClass } from '@/utils/orderStatus';

export default {
  name: "OrderList",
  props: {
    // 订单列表数据
    orders: {
      type: Array,
      default: () => [],
    },
    // 加载状态
    loading: {
      type: Boolean,
      default: false,
    },
    // 是否显示分页
    showPagination: {
      type: Boolean,
      default: true,
    },
    // 分页配置
    pagination: {
      type: Object,
      default: () => ({
        pageNum: 1,
        pageSize: 10,
        total: 0,
      }),
    },
  },
  data() {
    return {
      // 存储原始订单数据，用于详细信息切换
      originalOrderData: [],
      // 存储每个订单的当前激活 tab
      activeTabMap: {},
    };
  },
  methods: {
    // 获取订单状态样式类
    getOrderStatusClass(status) {
      return getOrderStatusClass(status);
    },

    // 获取订单的当前激活 tab
    getActiveTab(order) {
      const orderId = order.id || order.orderId;
      return this.activeTabMap[orderId] || null;
    },

    // 切换 tab（点击已激活的 tab 会收起）
    toggleTab(order, tab) {
      const orderId = order.id || order.orderId;
      const currentTab = this.activeTabMap[orderId];
      if (currentTab === tab) {
        // 再次点击同一个 tab，收起
        this.$set(this.activeTabMap, orderId, null);
      } else {
        // 点击不同 tab，切换
        this.$set(this.activeTabMap, orderId, tab);
      }
    },

    // 处理物流 tab 点击
    handleLogisticsTabClick(order) {
      const orderId = order.id || order.orderId;
      const currentTab = this.activeTabMap[orderId];
      if (currentTab === 'logistics') {
        // 再次点击，收起
        this.$set(this.activeTabMap, orderId, null);
      } else {
        // 展开物流信息
        this.$set(this.activeTabMap, orderId, 'logistics');
        // 触发加载物流信息
        this.$emit("load-logistics", order);
      }
    },

    // 切换详细信息显示
    toggleDetailInfo(order) {
      this.$emit("toggle-detail", order);
    },

    // 复制地址信息
    copyAddressInfo(order) {
      const addressInfo = `${order.idCardName} ${order.phone} ${order.province} ${order.city} ${order.town} ${order.address}`;
      this.copyText(addressInfo);
    },

    // 复制文本
    copyText(text) {
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            this.$message.success("复制成功");
          })
          .catch(() => {
            this.fallbackCopy(text);
          });
      } else {
        this.fallbackCopy(text);
      }
    },

    // 备用复制方法
    fallbackCopy(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        this.$message.success("复制成功");
      } catch (err) {
        this.$message.error("复制失败，请手动复制");
      }
      document.body.removeChild(textArea);
    },

    // 处理图片加载错误
    handleImageError(event) {
      event.target.src =
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg0NFY0NEgyMFYyMFoiIHN0cm9rZT0iI0NDQ0NDQyIgc3Ryb2tlLXdpZHRoPSIyIi8+CjxwYXRoIGQ9Ik0yOCAzMkwzMiAyOEwzNiAzMkw0MCAyOEw0NCAzMlY0MEgyOFYzMloiIGZpbGw9IiNDQ0NDQ0MiLz4KPC9zdmc+";
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.$emit("size-change", val);
    },

    // 当前页改变
    handleCurrentChange(val) {
      this.$emit("current-change", val);
    },
  },
};
</script><style s
coped>
/* 订单列表组件样式 */
.order-list-component {
  width: 100%;
  font-size: 12px;
  line-height: 1.4;
}

/* 加载状态 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #606266;
}

.loading-container i {
  font-size: 24px;
  color: #409eff;
  margin-bottom: 8px;
  animation: rotating 2s linear infinite;
}

.loading-container p {
  margin: 0;
  font-size: 14px;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 空状态 */
.empty-container {
  padding: 20px;
  text-align: center;
}

/* 订单列表 */
.order-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 订单卡片 */
.order-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.order-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 订单头部 */
.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #ebeef5;
}

.order-header-left {
  display: flex;
  align-items: center;
}

.order-number {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
}

.order-status {
  font-size: 13px;
  font-weight: 500;
}

/* 订单状态样式 */
.status-fail {
  color: #f56c6c;
  font-weight: 600;
}

.status-success {
  color: #67c23a;
  font-weight: 600;
}

.status-orange {
  color: #e6a23c;
  font-weight: 600;
}

.status-unknown {
  color: #909399;
  font-weight: 500;
}

.copy-btn {
  font-size: 10px !important;
  padding: 2px 6px !important;
  color: #409eff !important;
}

/* 订单内容 */
.order-content {
  padding: 12px;
}

/* 商品信息 */
.product-info {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.product-image {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #ebeef5;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-details {
  flex: 1;
  min-width: 0;
}

.product-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 6px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-subtitle {
  font-size: 12px;
  color: #606266;
  margin-bottom: 6px;
  line-height: 1.4;
}

.product-sku {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.product-sku .label {
  color: #909399;
}

.product-sku .value {
  color: #606266;
  font-family: monospace;
}

.copy-btn-inline {
  font-size: 10px !important;
  padding: 0 4px !important;
  color: #409eff !important;
  margin-left: 4px;
}

.product-unavailable {
  font-size: 14px;
  color: #909399;
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 12px;
}

/* 订单信息 */
.order-info {
  margin-top: 8px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

/* Tab 样式 */
.info-tabs {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #ebeef5;
}

.tab-item {
  flex: 1;
  padding: 8px 12px;
  text-align: center;
  font-size: 12px;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 2px solid transparent;
}

.tab-item:hover {
  color: #409eff;
  background: #ecf5ff;
}

.tab-item.active {
  color: #409eff;
  background: #fff;
  border-bottom-color: #409eff;
  font-weight: 500;
}

.tab-content {
  padding: 12px;
}

.info-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 12px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row .label {
  flex-shrink: 0;
  width: 80px;
  color: #909399;
  font-weight: 500;
}

.info-row .value {
  flex: 1;
  color: #606266;
  word-break: break-all;
}

/* 分页容器 */
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* Element UI 组件样式覆盖 */
.order-list-component >>> .el-tag,
.order-list-component ::v-deep .el-tag {
  font-size: 10px !important;
  padding: 0 6px !important;
  height: 20px !important;
  line-height: 18px !important;
}

.order-list-component >>> .el-button--mini,
.order-list-component ::v-deep .el-button--mini {
  font-size: 10px !important;
  padding: 2px 6px !important;
  min-height: 20px !important;
  line-height: 16px !important;
}

.order-list-component >>> .el-button--text,
.order-list-component ::v-deep .el-button--text {
  font-size: 10px !important;
  padding: 2px 4px !important;
  min-height: 16px !important;
  line-height: 12px !important;
}

.order-list-component >>> .el-pagination,
.order-list-component ::v-deep .el-pagination {
  font-size: 12px !important;
}

.order-list-component >>> .el-pagination .el-pager li,
.order-list-component ::v-deep .el-pagination .el-pager li {
  font-size: 12px !important;
  min-width: 24px !important;
  height: 24px !important;
  line-height: 24px !important;
}

.order-list-component >>> .el-pagination .btn-prev,
.order-list-component >>> .el-pagination .btn-next,
.order-list-component ::v-deep .el-pagination .btn-prev,
.order-list-component ::v-deep .el-pagination .btn-next {
  font-size: 12px !important;
  min-width: 24px !important;
  height: 24px !important;
  line-height: 24px !important;
}

/* 响应式设计 */
/* @media (max-width: 768px) {
  .order-card {
    margin: 0 -4px;
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
  
  .product-info {
    flex-direction: column;
    gap: 8px;
  }
  
  .product-image {
    align-self: center;
    width: 80px;
    height: 80px;
  }
  
  .info-row {
    flex-direction: column;
    gap: 4px;
  }
  
  .info-row .label {
    width: auto;
  }
} */

/* 暗色模式支持 */
/* @media (prefers-color-scheme: dark) {
  .order-card {
    background: #2d2d2d;
    border-color: #404040;
  }
  
  .order-header {
    background: #3a3a3a;
    border-color: #404040;
  }
  
  .order-number {
    color: #e0e0e0;
  }
  
  .product-name {
    color: #e0e0e0;
  }
  
  .product-unavailable {
    background: #3a3a3a;
    color: #aaa;
  }
  
  .info-row .label {
    color: #aaa;
  }
  
  .info-row .value {
    color: #ccc;
  }
} */
</style>